/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ImageResponse } from '../models/image-response';
import { ImageUpdateRequest } from '../models/image-update-request';

/**
 * Image resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class ImagesService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation updateImageProperties
   */
  static readonly UpdateImagePropertiesPath = '/images/{id}';

  /**
   * Update the requested image basic attributes.
   *
   * Update the requested image basic attributes
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateImageProperties()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateImageProperties$Response(
    params: {
      /**
       * Identifier of the image
       */
      id: string;
      body: ImageUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ImageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImagesService.UpdateImagePropertiesPath,
      'put'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ImageResponse>;
        })
      );
  }

  /**
   * Update the requested image basic attributes.
   *
   * Update the requested image basic attributes
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateImageProperties$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateImageProperties(
    params: {
      /**
       * Identifier of the image
       */
      id: string;
      body: ImageUpdateRequest;
    },
    context?: HttpContext
  ): Observable<ImageResponse> {
    return this.updateImageProperties$Response(params, context).pipe(
      map((r: StrictHttpResponse<ImageResponse>) => r.body as ImageResponse)
    );
  }

  /**
   * Path part for operation deleteImage1
   */
  static readonly DeleteImage1Path = '/images/{id}';

  /**
   * Delete an image by id.
   *
   * Delete image by id
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteImage1()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteImage1$Response(
    params: {
      /**
       * Identifier of the image
       */
      id: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImagesService.DeleteImage1Path,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete an image by id.
   *
   * Delete image by id
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteImage1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteImage1(
    params: {
      /**
       * Identifier of the image
       */
      id: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteImage1$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getImagesByTypeAndId
   */
  static readonly GetImagesByTypeAndIdPath = '/images';

  /**
   * Get images by refrence type and reference identifier.
   *
   * Finds and returns the saved images.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getImagesByTypeAndId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getImagesByTypeAndId$Response(
    params: {
      /**
       * Type of the referenced entity
       */
      type: 'PRODUCT' | 'CONTAINER' | 'PACKAGE';

      /**
       * ID of the referenced entity
       */
      id: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<ImageResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImagesService.GetImagesByTypeAndIdPath,
      'get'
    );
    if (params) {
      rb.query('type', params.type, {});
      rb.query('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<ImageResponse>>;
        })
      );
  }

  /**
   * Get images by refrence type and reference identifier.
   *
   * Finds and returns the saved images.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getImagesByTypeAndId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getImagesByTypeAndId(
    params: {
      /**
       * Type of the referenced entity
       */
      type: 'PRODUCT' | 'CONTAINER' | 'PACKAGE';

      /**
       * ID of the referenced entity
       */
      id: string;
    },
    context?: HttpContext
  ): Observable<Array<ImageResponse>> {
    return this.getImagesByTypeAndId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<ImageResponse>>) =>
          r.body as Array<ImageResponse>
      )
    );
  }

  /**
   * Path part for operation createImageToEntity
   */
  static readonly CreateImageToEntityPath = '/images';

  /**
   * Upload image to the referenced entity.
   *
   * Upload and saved the image to the referenced object.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createImageToEntity()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createImageToEntity$Response(
    params?: {
      body?: {
        /**
         * Image file resource
         */
        file: Blob;
        imageCreateRequest: Blob;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ImageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImagesService.CreateImageToEntityPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ImageResponse>;
        })
      );
  }

  /**
   * Upload image to the referenced entity.
   *
   * Upload and saved the image to the referenced object.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createImageToEntity$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createImageToEntity(
    params?: {
      body?: {
        /**
         * Image file resource
         */
        file: Blob;
        imageCreateRequest: Blob;
      };
    },
    context?: HttpContext
  ): Observable<ImageResponse> {
    return this.createImageToEntity$Response(params, context).pipe(
      map((r: StrictHttpResponse<ImageResponse>) => r.body as ImageResponse)
    );
  }
}
